import type { JSONContent } from '@tiptap/core'

import mixpanel from '@disco/api/mixpanel'
import { findTimestamps } from '@disco/shared-components/rich-text-editor'
import captureError from '@disco/utils/captureError'

import type { DiscussionVisibility } from 'types/discussion'

export const DISCUSSIONS_CONTEXT = 'Discussions'
const NOTIFICATION_SETTINGS_CONTEXT = 'Notification settings'

// Callers track inside the try blocks of successful saves, so a throw here
// would surface as a failed save and invite a duplicate retry
const withoutThrowing = <T>(run: () => T, fallback: T): T => {
  try {
    return run()
  } catch (error) {
    captureError(error as Error, { moduleName: 'DiscussionMixpanel' })
    return fallback
  }
}

export const DISCUSSION_EVENTS = {
  created: 'Discussion created',
  edited: 'Discussion edited',
  replied: 'Discussion replied',
  deleted: 'Discussion deleted',
  reaction: 'Discussion reaction',
  timestampClicked: 'Discussion timestamp clicked',
  notificationSettingChanged: 'Discussion notification setting changed',
  actionFailed: 'Discussion action failed',
  opened: 'Discussions opened',
} as const

export type DiscussionEventTarget = {
  target_type: string
  // Optional: a bulk create spans many objects
  target_object_id?: number | string
}

export type DiscussionEventContext = DiscussionEventTarget & {
  visibility: DiscussionVisibility
}

// Add an entry here for each new object type that gets discussions
const TARGET_IDENTITY: Record<string, { type: string; idKey: string }> = {
  track: { type: 'Track', idKey: 'Track ID' },
}

export const getDiscussionTargetProps = ({
  target_type,
  target_object_id,
}: DiscussionEventTarget): Record<string, number | string> => {
  const identity = TARGET_IDENTITY[target_type]
  if (!identity) return {}
  return {
    ...(target_object_id !== undefined
      ? { [identity.idKey]: target_object_id }
      : {}),
    Type: identity.type,
  }
}

type ContentCounts = {
  mention_count: number
  timestamp_count: number
  timestamp_range_count: number
}

const isTextBlock = (node: JSONContent): boolean =>
  (node.content ?? []).some(child => child.type === 'text')

const addCounts = (a: ContentCounts, b: ContentCounts): ContentCounts => ({
  mention_count: a.mention_count + b.mention_count,
  timestamp_count: a.timestamp_count + b.timestamp_count,
  timestamp_range_count: a.timestamp_range_count + b.timestamp_range_count,
})

const countTimestamps = (node: JSONContent): ContentCounts => {
  // Scanned per text node, like the editor does when it marks timestamps, so
  // only clickable timestamps are counted
  const matches = (node.content ?? [])
    .filter(child => child.type === 'text')
    .flatMap(child => findTimestamps(child.text ?? ''))
  return {
    mention_count: 0,
    timestamp_count: matches.length,
    timestamp_range_count: matches.filter(match => match.end !== null).length,
  }
}

export const countDiscussionContent = (node: JSONContent): ContentCounts => {
  const own: ContentCounts = isTextBlock(node)
    ? countTimestamps(node)
    : { mention_count: 0, timestamp_count: 0, timestamp_range_count: 0 }
  const withMention = {
    ...own,
    mention_count: own.mention_count + (node.type === 'mention' ? 1 : 0),
  }
  return (node.content ?? [])
    .map(countDiscussionContent)
    .reduce(addCounts, withMention)
}

// Timestamps are audio-only, so only tracks report them
export const getDiscussionContentProps = (
  target_type: string,
  body: JSONContent,
  { withRanges = false }: { withRanges?: boolean } = {},
): Partial<ContentCounts> =>
  withoutThrowing<Partial<ContentCounts>>(() => {
    const { mention_count, timestamp_count, timestamp_range_count } =
      countDiscussionContent(body)
    return {
      mention_count,
      ...(target_type === 'track'
        ? {
            timestamp_count,
            ...(withRanges ? { timestamp_range_count } : {}),
          }
        : {}),
    }
  }, {})

export const getErrorStatus = (error: unknown): number | undefined => {
  const status = (error as { status?: unknown } | null | undefined)?.status
  return typeof status === 'number' ? status : undefined
}

export const sendDiscussionEvent = (
  name: string,
  { visibility, ...target }: DiscussionEventContext,
  props: Record<string, unknown> = {},
): void =>
  withoutThrowing(() => {
    mixpanel.sendPureEvent(name, {
      ...getDiscussionTargetProps(target),
      Context: DISCUSSIONS_CONTEXT,
      visibility,
      ...props,
    })
  }, undefined)

type NotificationSettingSource = 'notes_tab' | 'settings_page'

const NOTIFICATION_SETTING_CONTEXT: Record<NotificationSettingSource, string> =
  {
    notes_tab: DISCUSSIONS_CONTEXT,
    settings_page: NOTIFICATION_SETTINGS_CONTEXT,
  }

// Only the per-track bell has a target; the settings page is account-wide
export const sendNotificationSettingEvent = (
  source: NotificationSettingSource,
  props: { setting: string; previous_value: unknown; new_value: unknown },
  target?: DiscussionEventTarget,
): void =>
  withoutThrowing(() => {
    mixpanel.sendPureEvent(DISCUSSION_EVENTS.notificationSettingChanged, {
      ...(target ? getDiscussionTargetProps(target) : {}),
      Context: NOTIFICATION_SETTING_CONTEXT[source],
      ...props,
    })
  }, undefined)
